type TradeOrderLike = {
  status?: string;
  fiat_payment_provider?: string;
  fiat_payment_status?: string;
  buyer_country?: string;
  currency_code?: string;
  amount_in_lumi?: number | string;
  flow_status_label?: string;
  flow_status_description?: string;
  collection_route_label?: string;
  collection_route?: string;
  payment_option_name?: string;
};

const KENYA_CODES = new Set(["KE", "KEN", "KENYA"]);
const GHANA_CODES = new Set(["GH", "GHA", "GHANA"]);

const COUNTRY_DIAL_CODES: Record<string, string> = {
  KE: "254", KEN: "254", KENYA: "254",
  GH: "233", GHA: "233", GHANA: "233",
};

export function normalizeMobileMoneyPhone(phone: string, country?: string): string {
  let digits = phone.replace(/\D/g, "");
  digits = digits.replace(/^0+/, "");
  const dialCode = COUNTRY_DIAL_CODES[String(country || "").toUpperCase()] || "";
  if (dialCode && !digits.startsWith(dialCode)) {
    digits = dialCode + digits;
  }
  return digits;
}

export function getPhonePlaceholder(country?: string): string {
  const c = String(country || "").toUpperCase();
  if (KENYA_CODES.has(c)) return "e.g. 254712345678";
  if (GHANA_CODES.has(c)) return "e.g. 233241234567";
  return "Enter phone number";
}
const CARD_KEYWORDS = ["card", "visa", "mastercard", "debit", "credit"];
const MOBILE_MONEY_KEYWORDS = ["mobile money", "momo", "mpesa", "m-pesa", "airtel", "tigo", "vodafone"];

export type BuyerPaymentCategory = "mobile_money" | "card";

type PaymentMethodLike = {
  id: string;
  name?: string;
  payment_option?: {
    name?: string;
    slug?: string;
  };
  account_details?: {
    provider_name?: string;
  };
};

export type BuyerPaymentMethodChoice = {
  key: string;
  title: string;
  description: string;
  category: BuyerPaymentCategory;
  routeLabel: string;
};

export function classifyBuyerPaymentMethod(
  paymentMethod: PaymentMethodLike | null | undefined,
): BuyerPaymentCategory | null {
  const name = String(
    paymentMethod?.payment_option?.name ||
      paymentMethod?.payment_option?.slug ||
      paymentMethod?.name ||
      paymentMethod?.account_details?.provider_name ||
      "",
  ).toLowerCase();

  if (CARD_KEYWORDS.some((keyword) => name.includes(keyword))) {
    return "card";
  }

  if (MOBILE_MONEY_KEYWORDS.some((keyword) => name.includes(keyword))) {
    return "mobile_money";
  }

  return null;
}

export function supportsMobileMoneyForCountry(country?: string) {
  const normalizedCountry = String(country || "").toUpperCase();
  return KENYA_CODES.has(normalizedCountry) || GHANA_CODES.has(normalizedCountry);
}

export function getLocalMobileMoneyCurrency(country?: string) {
  const normalizedCountry = String(country || "").toUpperCase();

  if (KENYA_CODES.has(normalizedCountry)) {
    return "KES";
  }

  if (GHANA_CODES.has(normalizedCountry)) {
    return "GHS";
  }

  return null;
}

export function getCollectionCurrency(category: BuyerPaymentCategory, country?: string): string | null {
  if (category === "mobile_money") return getLocalMobileMoneyCurrency(country);
  if (category === "card") {
    const c = String(country || "").toUpperCase();
    if (GHANA_CODES.has(c)) return "GHS";
  }
  return null;
}

export function supportsMobileMoneyForCurrency(country?: string, currencyCode?: string) {
  const localCurrency = getLocalMobileMoneyCurrency(country);
  return Boolean(localCurrency && String(currencyCode || "").toUpperCase() === localCurrency);
}

export function resolveCollectionRouteForCategory(
  paymentCategory: BuyerPaymentCategory,
  buyerCountry?: string,
) {
  const normalizedCountry = String(buyerCountry || "").toUpperCase();

  if (paymentCategory === "card") {
    return "Korba card collection";
  }

  if (KENYA_CODES.has(normalizedCountry)) {
    return "Synthera Kenya collection";
  }

  if (GHANA_CODES.has(normalizedCountry)) {
    return "Korba Ghana collection";
  }

  return "Mobile money collection unavailable";
}

function resolvePaymentMethodName(paymentMethod: PaymentMethodLike | null | undefined) {
  return String(
    paymentMethod?.payment_option?.name ||
      paymentMethod?.payment_option?.slug ||
      paymentMethod?.name ||
      paymentMethod?.account_details?.provider_name ||
      "",
  ).trim();
}

export function getBuyerPaymentMethodChoice(
  paymentMethod: PaymentMethodLike | null | undefined,
  buyerCountry?: string,
): BuyerPaymentMethodChoice | null {
  const name = resolvePaymentMethodName(paymentMethod);
  const normalizedName = name.toLowerCase();
  const normalizedCountry = String(buyerCountry || "").toUpperCase();

  if (CARD_KEYWORDS.some((keyword) => normalizedName.includes(keyword))) {
    return {
      key: `card:${paymentMethod?.id || normalizedName}`,
      title: "Pay with Card",
      description: "Use your debit or credit card. Korba card collection will be used automatically.",
      category: "card",
      routeLabel: "Korba card collection",
    };
  }

  if (!MOBILE_MONEY_KEYWORDS.some((keyword) => normalizedName.includes(keyword))) {
    return null;
  }

  if (GHANA_CODES.has(normalizedCountry)) {
    if (normalizedName.includes("mtn")) {
      return {
        key: `gh-mtn:${paymentMethod?.id || normalizedName}`,
        title: "Pay with MTN Mobile Money",
        description: "Use your MTN Mobile Money wallet for Ghana local collection.",
        category: "mobile_money",
        routeLabel: "Korba Ghana collection",
      };
    }

    if (
      normalizedName.includes("telecel") ||
      normalizedName.includes("vodafone") ||
      normalizedName.includes("vod")
    ) {
      return {
        key: `gh-telecel:${paymentMethod?.id || normalizedName}`,
        title: "Pay with Telecel Cash",
        description: "Use your Telecel Cash wallet for Ghana local collection.",
        category: "mobile_money",
        routeLabel: "Korba Ghana collection",
      };
    }

    if (normalizedName.includes("airtel") || normalizedName.includes("tigo")) {
      return {
        key: `gh-airteltigo:${paymentMethod?.id || normalizedName}`,
        title: "Pay with AirtelTigo Money",
        description: "Use your AirtelTigo Money wallet for Ghana local collection.",
        category: "mobile_money",
        routeLabel: "Korba Ghana collection",
      };
    }

    return {
      key: `gh-momo:${paymentMethod?.id || normalizedName}`,
      title: "Pay with Mobile Money",
      description: "Use your supported mobile money account for Ghana local collection.",
      category: "mobile_money",
      routeLabel: "Korba Ghana collection",
    };
  }

  if (KENYA_CODES.has(normalizedCountry)) {
    if (normalizedName.includes("mpesa") || normalizedName.includes("m-pesa")) {
      return {
        key: `ke-mpesa:${paymentMethod?.id || normalizedName}`,
        title: "Pay with M-PESA",
        description: "Use your M-PESA wallet for Kenya local collection.",
        category: "mobile_money",
        routeLabel: "Synthera Kenya collection",
      };
    }

    if (normalizedName.includes("airtel")) {
      return {
        key: `ke-airtel:${paymentMethod?.id || normalizedName}`,
        title: "Pay with Airtel Money",
        description: "Use your Airtel Money wallet for Kenya local collection.",
        category: "mobile_money",
        routeLabel: "Synthera Kenya collection",
      };
    }

    return {
      key: `ke-momo:${paymentMethod?.id || normalizedName}`,
      title: "Pay with Mobile Money",
      description: "Use your supported mobile money account for Kenya local collection.",
      category: "mobile_money",
      routeLabel: "Synthera Kenya collection",
    };
  }

  return null;
}

export function resolveCollectionRouteLabel(order: TradeOrderLike, fallbackCountry?: string) {
  if (order?.collection_route_label) {
    return order.collection_route_label;
  }

  const provider = String(order?.fiat_payment_provider || "").toLowerCase();
  const buyerCountry = String(order?.buyer_country || fallbackCountry || "").toUpperCase();
  const paymentOptionName = String(order?.payment_option_name || "").toLowerCase();

  if (CARD_KEYWORDS.some((keyword) => paymentOptionName.includes(keyword))) {
    return "Korba card collection";
  }

  if (provider === "synthera" || KENYA_CODES.has(buyerCountry)) {
    return "Synthera Kenya collection";
  }

  if (buyerCountry === "GH") {
    return "Korba Ghana collection";
  }

  return "Korba card collection";
}

export function resolveFlowCopy(order: TradeOrderLike) {
  if (order?.flow_status_label && order?.flow_status_description) {
    return {
      label: order.flow_status_label,
      description: order.flow_status_description,
    };
  }

  const status = String(order?.status || "");
  const fiatStatus = String(order?.fiat_payment_status || "");

  if (status === "Completed") {
    return {
      label: "Trade completed",
      description:
        "Buyer debit, buyer AKL credit, and seller wallet settlement have all completed successfully.",
    };
  }

  if (status === "Failed") {
    return {
      label: "Collection failed",
      description:
        "The provider collection step failed before the trade could settle completely.",
    };
  }

  if (status === "Cancelled") {
    return {
      label: "Trade cancelled",
      description: "This order was cancelled before the provider-led settlement finished.",
    };
  }

  if (status === "Conflict") {
    return {
      label: "Settlement needs review",
      description:
        "A settlement exception occurred and Hanypay needs to review this trade before it can close.",
    };
  }

  if (fiatStatus === "Initiated" || fiatStatus === "Pending") {
    return {
      label: "Buyer debit in progress",
      description:
        "Hanypay has already selected the payment rail and is waiting for provider debit confirmation.",
    };
  }

  return {
    label: "Awaiting payment",
    description:
      "The trade has been created and is waiting for provider collection to complete.",
  };
}

export function getSettlementTimeline(order: TradeOrderLike, fallbackCountry?: string) {
  const routeLabel = resolveCollectionRouteLabel(order, fallbackCountry);
  const status = String(order?.status || "");
  const fiatStatus = String(order?.fiat_payment_status || "");

  const isComplete = status === "Completed";
  const isFailed = ["Failed", "Cancelled", "Conflict"].includes(status);
  const buyerDebitComplete = isComplete || fiatStatus === "Completed";
  const buyerDebitActive =
    !isFailed &&
    !buyerDebitComplete &&
    (fiatStatus === "Initiated" || fiatStatus === "Pending" || status === "Pending");
  const buyerAssetComplete = isComplete;
  const sellerSettlementComplete = isComplete;

  return [
    {
      title: "Route selected",
      description: routeLabel,
      state: "complete",
    },
    {
      title: "Buyer debit",
      description: `Collecting buyer funds through ${routeLabel}.`,
      state: isFailed ? "failed" : buyerDebitComplete ? "complete" : buyerDebitActive ? "current" : "pending",
    },
    {
      title: "Buyer AKL credit",
      description: `Credit ${Number(order?.amount_in_lumi || 0).toFixed(2)} AKL to the buyer wallet.`,
      state: isFailed ? "failed" : buyerAssetComplete ? "complete" : buyerDebitComplete ? "current" : "pending",
    },
    {
      title: "Seller wallet credit",
      description: `Credit the seller ${order?.currency_code || ""} wallet with settlement proceeds.`,
      state:
        isFailed
          ? "failed"
          : sellerSettlementComplete
            ? "complete"
            : buyerAssetComplete
              ? "current"
              : "pending",
    },
  ];
}
